import type { SelectOption } from '@poseidon/components/Input2';
import { isNotEmpty } from '@poseidon/components/common';

/**
 * Signup form validation (POS-2510). Lives outside `src/pages` because every file there is a
 * route in the Pages Router — this module and its test must not become `/signupValidation`.
 */

export const ATTRIBUTION_VALUES = ['Paid Media', 'Search', 'My agency uses it', 'G2', 'Customer reference'] as const;

export type AttributionValue = (typeof ATTRIBUTION_VALUES)[number];

export const attributionOptions: SelectOption[] = ATTRIBUTION_VALUES.map((value) => ({
  label: value,
  value,
}));

const validAttributionValues = new Set<string>(ATTRIBUTION_VALUES);

export function isValidAttribution(value: string): value is AttributionValue {
  return validAttributionValues.has(value);
}

export type SignupFormValues = {
  name?: string;
  email?: string;
  password?: string;
  attribution?: string;
};

export type SignupFormErrors = {
  name?: string;
  email?: string;
  password?: string;
  attribution?: string;
};

export type SignupValidation =
  | { ok: true; values: { name: string; email: string; password: string; attribution: AttributionValue } }
  | { ok: false; errors: SignupFormErrors };

/**
 * Validate every field at once so a customer sees all problems on the first submit, instead of
 * discovering them one per round-trip (which, for a pre-provisioned address, made the later
 * "email already registered" look like the earlier attempt had half-registered them).
 */
export function validateSignupForm(values: SignupFormValues): SignupValidation {
  const errors: SignupFormErrors = {};
  const { name, email, password, attribution } = values;

  if (!isNotEmpty(name)) errors.name = 'Oops - Please tell us who you are?';
  if (!isNotEmpty(email)) errors.email = 'We need your email to get started';
  if (!isNotEmpty(password)) errors.password = 'Invalid password';
  else if (password.length < 8) errors.password = 'Password have to be longer';
  if (!isNotEmpty(attribution)) errors.attribution = 'This field is required';
  else if (!isValidAttribution(attribution)) errors.attribution = 'Please select a valid option';

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  // Unreachable when `errors` is empty; restated so TypeScript narrows the values it returns.
  if (
    !isNotEmpty(name) ||
    !isNotEmpty(email) ||
    !isNotEmpty(password) ||
    !isNotEmpty(attribution) ||
    !isValidAttribution(attribution)
  ) {
    return { ok: false, errors };
  }
  return { ok: true, values: { name, email, password, attribution } };
}
